import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogContent,
  Box,
  Divider,
  IconButton,
  Grid
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import StraightenIcon from '@mui/icons-material/Straighten';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import Iconify from './Iconify';
import moment from 'moment';

const BookingDetailsCard = ({ booking, onClose, open }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent style={{ padding: 0 }}>
        <Card
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 1
          }}
        >
          <CardContent sx={{ p: '15px 20px 0px 20px' }}>
            {/* Header */}
            <Box
              sx={{
                color: 'white',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 1
              }}
            >
              <Typography
                variant="h5"
                textAlign="center"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  color: '#2a2a2a',
                  textTransform: 'uppercase',
                  letterSpacing: 1
                }}
              >
                Booking Details
              </Typography>
              <IconButton onClick={onClose} sx={{ marginTop: -0.5 }}>
                <Iconify icon="ic:round-clear" style={{ fontSize: 19, color: '#2a2a2a' }} />
              </IconButton>
            </Box>

            {/* Data Lines */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box style={{ position: 'relative' }}>
                <Box style={{ position: 'relative' }}>
                  {/* Row 1 */}
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start', // Align text and icons properly
                      height: 60
                    }}
                  >
                    <Box
                      style={{
                        width: 13, // Fixed icon width
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginRight: 6
                      }}
                    >
                      <Iconify
                        icon="f7:dot-square-fill"
                        style={{
                          fontSize: 13, // Fixed icon size
                          color: '#010f58'
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      style={{
                        marginTop: -5,
                        lineHeight: '1.5', // Ensure consistent line spacing
                        overflow: 'hidden', // Handle overflow
                        textOverflow: 'ellipsis', // Add ellipsis if text is too long
                        display: '-webkit-box', // Enable line clamping
                        WebkitLineClamp: 3, // Limit to 3 lines
                        WebkitBoxOrient: 'vertical', // Required for line clamping,
                        color: '#000'
                      }}
                    >
                      {booking?.location_origin}
                    </Typography>
                  </Box>

                  {/* Row 2 */}
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start', // Align text and icons properly
                      height: 60
                    }}
                  >
                    <Box
                      style={{
                        width: 13, // Fixed icon width
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginRight: 6
                      }}
                    >
                      <Iconify
                        icon="f7:square-fill"
                        style={{
                          fontSize: 13, // Fixed icon size
                          color: '#010f58'
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      style={{
                        marginTop: -5,
                        lineHeight: '1.5', // Ensure consistent line spacing
                        overflow: 'hidden', // Handle overflow
                        textOverflow: 'ellipsis', // Add ellipsis if text is too long
                        display: '-webkit-box', // Enable line clamping
                        WebkitLineClamp: 3, // Limit to 3 lines
                        WebkitBoxOrient: 'vertical', // Required for line clamping,
                        color: '#000'
                      }}
                    >
                      {booking?.location_destination}
                    </Typography>
                  </Box>
                </Box>

                {/* Vertical Icons */}
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: 'column',
                    position: 'absolute',
                    top: 13.5,
                    left: 0.5
                  }}
                >
                  {Array(4)
                    .fill(null)
                    .map((_, idx) => (
                      <Iconify
                        key={idx}
                        icon="pepicons-pop:line-y"
                        style={{
                          fontSize: 7, // Fixed line size
                          color: '#010f58',
                          marginBottom: 2
                        }}
                      />
                    ))}
                  <Iconify
                    icon="ion:arrow-down-outline"
                    style={{
                      fontSize: 11, // Fixed arrow size
                      color: '#010f58'
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginTop: -3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Iconify
                    icon="material-symbols:social-distance-rounded"
                    sx={{ fontSize: 25, color: '#010f58', mr: 1, ml: -0.3 }}
                  />
                  <Typography variant="body1">
                    <strong>{booking?.total_mil} km </strong>
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Iconify
                    icon="fa6-solid:truck-fast"
                    sx={{ fontSize: 20, color: '#010f58', mr: 1, ml: -0.3 }}
                  />
                  <Typography variant="body1">
                    {booking?.equipment_type} | {booking?.equipment_name} | {booking?.length_ft}{' '}
                    {booking?.length_unit} | {booking?.weight_lbs} {booking?.weight_unit}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <CalendarTodayIcon sx={{ color: '#010f58', mr: 1 }} />
                  <Typography variant="body1">
                    {moment(booking?.pickup_earliest).format('MM/DD/YYYY')} -{' '}
                    {moment(booking?.pickup_lattest).format('MM/DD/YYYY')}
                  </Typography>
                </Box>
              </Box>

              {/* Line 5 - Rate & Ref */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <AttachMoneyIcon sx={{ color: '#010f58', mr: 1 }} />
                  <Typography variant="body1">{booking?.rate}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Iconify icon="ix:reference" sx={{ fontSize: 25, color: '#010f58', mr: 1 }} />
                  <Typography variant="body1">{booking?.reference_id}</Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDetailsCard;
